import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, ArrowRight, ShieldCheck, Truck, RotateCcw, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { api, formatPrice } from "../lib/api";
import Seo from "../components/Seo";

const initial = {
  full_name: "",
  email: "",
  phone: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "United States",
};

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, shipping, tax, total } = useCart();
  const [form, setForm] = useState(initial);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastCapturedEmailRef = useRef("");

  // Capture abandoned-cart record once the customer enters a valid email.
  // Debounced so we don't spam the API on every keystroke.
  useEffect(() => {
    const email = (form.email || "").trim().toLowerCase();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!valid || items.length === 0) return;
    if (lastCapturedEmailRef.current === email) return;
    const handle = setTimeout(() => {
      lastCapturedEmailRef.current = email;
      api
        .post("/abandoned-carts", {
          email,
          items: items.map((i) => ({
            product_id: i.product_id,
            name: i.name,
            price: i.price,
            quantity: i.quantity,
            image: i.image,
          })),
        })
        .catch(() => {
          // Silent — recovery emails are best-effort.
          lastCapturedEmailRef.current = "";
        });
    }, 1200);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.email, items.length]);

  if (items.length === 0) {
    return (
      <div className="pt-40 pb-24 text-center" data-testid="checkout-empty">
        <Seo title="Checkout" noindex />
        <p className="font-display text-4xl">No bags to checkout.</p>
        <Link to="/shop" className="btn-primary mt-8 inline-flex">Browse the Edit</Link>
      </div>
    );
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      toast.error("Please accept the Terms & Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          image: i.image,
        })),
        shipping: form,
        origin_url: window.location.origin,
        accepted_terms: acceptedTerms,
        marketing_consent: marketingConsent,
      };
      const r = await api.post("/checkout/session", payload);
      if (r.data && r.data.url) {
        // Redirect to Stripe hosted checkout
        window.location.href = r.data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start payment. Try again.");
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 pb-24" data-testid="checkout-page">
      <Seo title="Checkout" noindex />
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="mb-12">
          <p className="eyebrow">Checkout</p>
          <h1 className="font-display text-5xl md:text-7xl mt-3 tracking-tighter">Almost yours.</h1>
        </div>

        <form onSubmit={submit} className="grid grid-cols-12 gap-8 md:gap-16" data-testid="checkout-form">
          <div className="col-span-12 md:col-span-7 space-y-12">
            <Section title="Contact" eyebrow="01">
              <Field label="Full name" required value={form.full_name} onChange={set("full_name")} testid="ck-full-name" />
              <Field label="Email" type="email" required value={form.email} onChange={set("email")} testid="ck-email" />
              <Field label="Phone" value={form.phone} onChange={set("phone")} testid="ck-phone" />
            </Section>

            <Section title="Shipping address" eyebrow="02">
              <div className="-mt-2 mb-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[var(--mn-ink-2)]" data-testid="checkout-us-only-notice">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--mn-accent)]" />
                Shipping within the United States only
              </div>
              <Field label="Address line 1" required value={form.address_line1} onChange={set("address_line1")} testid="ck-addr1" />
              <Field label="Address line 2" value={form.address_line2} onChange={set("address_line2")} testid="ck-addr2" />
              <div className="grid grid-cols-2 gap-4">
                <Field label="City" required value={form.city} onChange={set("city")} testid="ck-city" />
                <Field label="State / Region" value={form.state} onChange={set("state")} testid="ck-state" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Postal code" required value={form.postal_code} onChange={set("postal_code")} testid="ck-zip" />
                <label className="block">
                  <span className="eyebrow block mb-2">Country</span>
                  <input
                    type="text"
                    value="United States"
                    readOnly
                    disabled
                    className="field-input bg-neutral-100 text-[var(--mn-ink-2)] cursor-not-allowed"
                    data-testid="ck-country"
                  />
                </label>
              </div>
            </Section>

            <Section title="Payment" eyebrow="03">
              <div className="bg-[var(--mn-ink)] text-[var(--mn-bg)] p-6 flex items-start gap-3" data-testid="checkout-stripe-notice">
                <Lock size={18} strokeWidth={1.4} className="mt-0.5" />
                <div>
                  <p className="text-sm font-medium uppercase tracking-[0.18em]">Secure payment by Stripe</p>
                  <p className="text-xs text-white/70 mt-1 leading-relaxed">
                    You'll be redirected to Stripe's encrypted checkout to complete your payment.
                    Cards, Apple Pay, and Google Pay are supported. We never see or store your card details.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-[var(--mn-ink-2)] mt-4">
                <ShieldCheck size={14} strokeWidth={1.5} />
                100% Authentic · Money-back guarantee · 14-day returns
              </div>
            </Section>
          </div>

          <aside className="col-span-12 md:col-span-5 md:sticky md:top-28 self-start" data-testid="checkout-summary">
            <div className="border border-[var(--mn-ink)] p-8">
              <p className="eyebrow">Order</p>
              <h3 className="font-display text-3xl mt-2">{items.length} bag{items.length === 1 ? "" : "s"}</h3>

              <ul className="mt-8 divide-y divide-[var(--mn-border)]">
                {items.map((it) => (
                  <li key={it.product_id} className="py-4 flex gap-4">
                    <div className="w-16 h-20 bg-neutral-100 overflow-hidden shrink-0">
                      {it.image && <img src={it.image} alt={it.name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-lg leading-tight">{it.name}</p>
                      <p className="text-xs text-[var(--mn-ink-2)] mt-1">Qty {it.quantity}</p>
                    </div>
                    <p className="font-mono-luxe text-sm shrink-0">{formatPrice(it.price * it.quantity)}</p>
                  </li>
                ))}
              </ul>

              <dl className="mt-8 space-y-3 text-sm border-t border-[var(--mn-border)] pt-6">
                <Row label="Subtotal" value={formatPrice(subtotal)} />
                <Row label="Shipping" value={shipping === 0 ? "Free" : formatPrice(shipping)} />
                <Row label="Tax" value={formatPrice(tax)} />
                <div className="border-t border-[var(--mn-border)] pt-3 flex justify-between items-baseline">
                  <dt className="eyebrow">Total</dt>
                  <dd className="font-mono-luxe text-xl" data-testid="checkout-total">{formatPrice(total)}</dd>
                </div>
              </dl>

              {/* Trust badges — boost conversion at the moment of payment */}
              <div
                className="mt-8 border border-[var(--mn-border-strong)] grid grid-cols-2"
                data-testid="checkout-trust-badges"
              >
                <TrustBadge
                  icon={<Lock size={16} strokeWidth={1.4} />}
                  title="Stripe Secure"
                  subtitle="256-bit SSL encrypted"
                  testid="trust-badge-stripe"
                />
                <TrustBadge
                  icon={<Truck size={16} strokeWidth={1.4} />}
                  title="Free Shipping"
                  subtitle="On orders over $200"
                  testid="trust-badge-shipping"
                  borderLeft
                />
                <TrustBadge
                  icon={<RotateCcw size={16} strokeWidth={1.4} />}
                  title="14-Day Returns"
                  subtitle="Money-back guarantee"
                  testid="trust-badge-returns"
                  borderTop
                />
                <TrustBadge
                  icon={<BadgeCheck size={16} strokeWidth={1.4} />}
                  title="100% Authentic"
                  subtitle="Hand-authenticated"
                  testid="trust-badge-authentic"
                  borderLeft
                  borderTop
                />
              </div>

              <div className="mt-6 space-y-3" data-testid="checkout-consent">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    required
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[var(--mn-ink)] cursor-pointer shrink-0"
                    data-testid="checkout-terms-checkbox"
                  />
                  <span className="text-xs leading-relaxed text-[var(--mn-ink)]">
                    I have read and agree to the{" "}
                    <Link to="/terms" target="_blank" className="underline underline-offset-4 decoration-[var(--mn-accent)] hover:text-[var(--mn-accent)]" data-testid="checkout-terms-link">
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link to="/privacy" target="_blank" className="underline underline-offset-4 decoration-[var(--mn-accent)] hover:text-[var(--mn-accent)]" data-testid="checkout-privacy-link">
                      Privacy Policy
                    </Link>
                    . <span className="text-[var(--mn-accent)]">*</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[var(--mn-ink)] cursor-pointer shrink-0"
                    data-testid="checkout-marketing-checkbox"
                  />
                  <span className="text-xs leading-relaxed text-[var(--mn-ink-2)]">
                    Send me first-access editorials &amp; new arrivals. No spam, unsubscribe anytime.
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={loading || !acceptedTerms}
                className="btn-primary w-full justify-center mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="checkout-submit"
              >
                {loading ? "Redirecting…" : "Pay with Stripe"}
                {!loading && <ArrowRight size={16} strokeWidth={1.5} />}
              </button>
              <p className="text-[11px] text-[var(--mn-ink-2)] mt-4">
                You'll be redirected to Stripe to complete payment securely.
              </p>
            </div>
          </aside>
        </form>
      </div>
    </div>
  );
}

function Section({ title, eyebrow, children }) {
  return (
    <section>
      <div className="flex items-baseline gap-4 mb-6">
        <span className="font-mono-luxe text-xs text-[var(--mn-muted)]">{eyebrow}</span>
        <h2 className="font-display text-3xl tracking-tight">{title}</h2>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      <input className="field-input" data-testid={testid} {...props} />
    </label>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <dt className="text-[var(--mn-ink-2)]">{label}</dt>
      <dd className="font-mono-luxe">{value}</dd>
    </div>
  );
}

function TrustBadge({ icon, title, subtitle, testid, borderLeft, borderTop }) {
  return (
    <div
      className={`p-3.5 flex items-start gap-2.5 ${borderLeft ? "border-l border-[var(--mn-border-strong)]" : ""} ${borderTop ? "border-t border-[var(--mn-border-strong)]" : ""}`}
      data-testid={testid}
    >
      <span className="text-[var(--mn-ink)] shrink-0 mt-0.5">{icon}</span>
      <div className="min-w-0">
        <p className="text-[10px] font-medium uppercase tracking-[0.18em] leading-tight text-[var(--mn-ink)]">
          {title}
        </p>
        <p className="text-[10px] mt-1 leading-snug text-[var(--mn-ink-2)]">{subtitle}</p>
      </div>
    </div>
  );
}
